import type { Artist } from '@/types';
import { Badge } from '@/components/ui/badge';
import { Check } from 'lucide-react';
import { toTitleCase } from '@/lib/utils';

interface ArtistCardProps {
  artist: Artist;
  selected: boolean;
  onSelect: () => void;
}

export function ArtistCard({ artist, selected, onSelect }: ArtistCardProps) {
  const priceRange = artist.priceRange || getPriceRange(artist.prices);

  return (
    <button
      onClick={onSelect}
      className={`
        relative w-full text-left rounded-xl overflow-hidden
        border-2 transition-all duration-200
        ${selected
          ? 'border-[var(--color-primary)] ring-2 ring-[var(--color-primary)]/20'
          : 'border-[var(--color-border)] hover:border-[var(--color-primary)]/50'
        }
        bg-[var(--color-bg-card)]
        focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]
      `}
    >
      {artist.coverImage && (
        <div className="h-24 w-full overflow-hidden">
          <img
            src={artist.coverImage}
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
      )}

      <div className="p-4">
        <div className="flex items-start gap-3">
          {artist.profilePicture ? (
            <img
              src={artist.profilePicture}
              alt={artist.name}
              className="w-12 h-12 rounded-full object-cover border-2 border-[var(--color-border)]"
            />
          ) : (
            <div className="w-12 h-12 rounded-full bg-[var(--color-primary)]/10 flex items-center justify-center">
              <span className="text-lg font-bold text-[var(--color-primary)]">
                {artist.name.charAt(0)}
              </span>
            </div>
          )}

          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-[var(--color-text)] truncate">
              {artist.name}
            </h3>
            <p className="text-sm text-[var(--color-text-muted)]">
              {priceRange}
            </p>
          </div>
        </div>

        {artist.tags && artist.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-3">
            {artist.tags.slice(0, 3).map(tag => (
              <Badge
                key={tag}
                variant="secondary"
                className="text-xs"
              >
                {toTitleCase(tag)}
              </Badge>
            ))}
          </div>
        )}
      </div>

      {selected && (
        <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-[var(--color-primary)] flex items-center justify-center">
          <Check className="w-4 h-4 text-white" />
        </div>
      )}

      <StatusBadge status={artist.status} />
    </button>
  );
}

function StatusBadge({ status }: { status: string }) {
  if (status === 'open') return null;

  return (
    <div className="absolute top-2 left-2">
      <Badge
        variant={status === 'waitlist' ? 'secondary' : 'destructive'}
        className="text-xs"
      >
        {status === 'waitlist' ? 'Waitlist' : 'Closed'}
      </Badge>
    </div>
  );
}

function getPriceRange(prices: Record<string, number | string> | null): string {
  if (!prices || Object.keys(prices).length === 0) return 'Contact for pricing';

  const values = Object.values(prices)
    .map(p => {
      if (typeof p === 'number') return p;
      const parsed = parseInt(String(p).replace(/[^0-9.-]/g, ''), 10);
      return isNaN(parsed) ? 0 : parsed;
    })
    .filter(n => n > 0);

  if (values.length === 0) return 'Contact for pricing';

  const min = Math.min(...values);
  const max = Math.max(...values);

  if (min === max) return `From $${min}`;
  return `$${min} - $${max}`;
}
